/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import { ConceptList } from './ConceptList';
import type { DataExplorerTypes } from '../../store';
import { dataExplorerSelectors } from '../../store';
import type {
  Concept,
  DataExplorerModuleState,
  DiscoveryMetadataItems,
} from '../../store/types';
import { useFetchItems } from '../../utils/dataexplorer-api/hooks';

// Get the concept ids that are included in the displayed datasets
const getConceptIdsInDatasets = (
  concepts: Concept[],
  datasets: DiscoveryMetadataItems,
): string[] => {
  const shownConceptIds = concepts.map((concept) => concept.id);
  const foundConceptIds = new Set<string>();

  datasets.features.forEach((dataset) => {
    dataset.properties.themes?.forEach((theme) => {
      theme.concepts?.forEach((concept) => {
        if (shownConceptIds.includes(concept.id)) {
          foundConceptIds.add(concept.id);
        }
      });
    });
  });

  return Array.from(foundConceptIds);
};

export const ConceptListConnect: React.FC = () => {
  const concepts = useSelector(
    (store: DataExplorerTypes.DataExplorerModuleState) =>
      dataExplorerSelectors.getConcepts(store),
  );

  const baseUrl = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getDiscoveryCatalogueURL(store),
  );

  const searchFilter = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getSearchFilter(store),
  );

  const queryables = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getQueryables(store),
  );

  const bbox = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getBbox(store),
  );

  const meteogateOnly = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getMeteogateOnly(store),
  );

  const { data: datasets, isFetching: loading } = useFetchItems(
    baseUrl,
    searchFilter,
    queryables,
    [],
    bbox,
    meteogateOnly,
  );

  const datasetConcepts = React.useMemo(() => {
    const selectedQueryables = queryables.some(
      (queryable) => queryable.selected.length > 0,
    );
    const bboxIsFiltered =
      bbox && bbox.length > 0 && bbox.toString() !== '-180,-90,180,90';

    if (
      !loading &&
      datasets &&
      (searchFilter !== '' || selectedQueryables || bboxIsFiltered)
    ) {
      return getConceptIdsInDatasets(concepts, datasets);
    }
    return [];
  }, [loading, concepts, datasets, searchFilter, queryables, bbox]);

  return <ConceptList concepts={concepts} datasetConcepts={datasetConcepts} />;
};
